import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import UIButton from 'Components/UIComponents/UIButton';
import { useAccessTokenInit } from 'Hooks/Auth';
import { useSigningRemindersUnlink } from 'Hooks/DocumentSign';
import { UnauthorizedRoutePaths } from 'Interfaces/RoutePaths';
import Toast from 'Services/Toast';

type PageParams = {
  signerId: string;
};

const UnlinkRemindersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<PageParams>();
  const [initAccessToken, finishInitAccessToken] = useAccessTokenInit();
  const [unlinkReminders, isLoading] = useSigningRemindersUnlink();

  const { signToken, signerId } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const signerId = params.signerId;

    return {
      signToken: searchParams.get('accessToken'),
      signerId,
    };
  }, [location.search, params]);

  const navigateToRoot = useCallback(() => {
    navigate(UnauthorizedRoutePaths.BASE_PATH, { replace: true });
  }, [navigate]);

  const handleUnlinkReminders = useCallback(
    async (signerId: string) => {
      try {
        await unlinkReminders({ signerId });
        Toast.success('Reminders stopped.');
      } catch (error) {
        navigateToRoot();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!signToken || !signerId) {
      return navigateToRoot();
    }

    initAccessToken({ token: signToken });
    handleUnlinkReminders(signerId);

    return () => finishInitAccessToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleUnlinkReminders, navigateToRoot, signToken]);

  return (
    <div className="team__accept-wrapper">
      <div className="team__accept">
        <h1 className="team__accept-title">
          You have been unlinked from reminders for the document
        </h1>
        <div className="team__accept-button">
          <UIButton
            priority="primary"
            title="Back to main page"
            handleClick={navigateToRoot}
            disabled={isLoading}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default UnlinkRemindersPage;
